/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */
import { BillingTypeEnum } from "@/lib/enum/billingType.enum";
import billingService from "@/services/thg/services/billingService";
import {
  ThgAccountingRecordItemViewmodelGen,
  ThgBillingViewmodelGen,
  ThgCreateBillingThgDtoGen,
  ThgOperationIdViewModelGen,
  ThgPublishBillingsDtoGen,
  ThgThgViewModelGen,
  ThgUrlViewmodelGen
} from "@/services/thg/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

@Module({
  dynamic: true,
  namespaced: true,
  name: "billing",
  store
})
export class BillingStore extends VuexModule {
  private _myBillings: ThgBillingViewmodelGen[] = [];

  private _readyForBilling: ThgThgViewModelGen[] = [];

  private _readyForBillingLoading = false;

  private _selectedThgsForBilling: ThgThgViewModelGen[] = [];

  private _createdBillings: any[] = [];

  private _createdBilling: any = {};

  private _billingPdf = "";

  private _billingAccounting = "";

  private _allBillings: any[] = [];

  get myBillings(): ThgBillingViewmodelGen[] {
    return this._myBillings;
  }

  get readyForBilling(): ThgThgViewModelGen[] {
    return this._readyForBilling;
  }

  get readyForBillingLoading(): boolean {
    return this._readyForBillingLoading;
  }

  get selectedThgsForBilling(): ThgThgViewModelGen[] {
    return this._selectedThgsForBilling;
  }

  get createdBillings(): any[] {
    return this._createdBillings;
  }

  get createdBilling(): any {
    return this._createdBilling;
  }

  get billingPdf(): string {
    return this._billingPdf;
  }

  get billingAccounting(): any {
    return this._billingAccounting;
  }

  get allBillings(): any[] {
    return this._allBillings;
  }

  @Mutation
  mutateMyBillings(myBillings: ThgBillingViewmodelGen[]): void {
    this._myBillings = myBillings;
  }

  @Mutation
  mutateReadyForBilling(readyForBilling: ThgThgViewModelGen[]) {
    this._readyForBilling = readyForBilling;
  }

  @Mutation
  mutateReadyForBillingLoading(readyForBillingLoading: boolean) {
    this._readyForBillingLoading = readyForBillingLoading;
  }

  @Mutation
  mutateSelectedThgsForBilling(selectedThgsForBilling: ThgThgViewModelGen[]) {
    this._selectedThgsForBilling = selectedThgsForBilling;
  }

  @Mutation
  mutateCreatedBillings(createdBillings: any[]) {
    this._createdBillings = createdBillings;
  }

  @Mutation
  mutateCreatedBilling(createdBilling: any) {
    this._createdBilling = createdBilling;
  }

  @Mutation
  mutateBillingPdf(billingPdf: string) {
    this._billingPdf = billingPdf;
  }

  @Mutation
  mutateBillingAccounting(billingAccounting: any) {
    this._billingAccounting = billingAccounting;
  }

  @Mutation
  mutateAllBillings(allBillings: any[]) {
    this._allBillings = allBillings;
  }

  @Action
  async setMyBillings(): Promise<void> {
    const myBillings = await billingService.getMyBillings();
    this.context.commit("mutateMyBillings", myBillings);
  }

  @Action
  async getReadyForBilling(data: { billingType: BillingTypeEnum; from: string; to: string }): Promise<void> {
    this.context.commit("mutateReadyForBillingLoading", true);
    const readyForBilling = await billingService.getThgsReadyForBilling(data.billingType, data.from, data.to);
    this.context.commit("mutateReadyForBilling", readyForBilling);
    this.context.commit("mutateReadyForBillingLoading", false);
  }

  @Action
  async setReadyForBilling(readyForBilling: ThgThgViewModelGen[]): Promise<void> {
    this.context.commit("mutateReadyForBillingLoading", true);
    this.context.commit("mutateReadyForBilling", readyForBilling);
    this.context.commit("mutateReadyForBillingLoading", false);
  }

  @Action
  async setReadyForBillingLoading(loaded: boolean): Promise<void> {
    this.context.commit("mutateReadyForBillingLoading", loaded);
  }

  @Action
  setSelectedThgsForBilling(selectedThgsForBilling: ThgThgViewModelGen[]): void {
    this.context.commit("mutateSelectedThgsForBilling", selectedThgsForBilling);
  }

  @Action
  setCreatedBillings(createdBillings: any[]): void {
    this.context.commit("mutateCreatedBillings", createdBillings);
  }

  @Action
  addToCreatedBillings(data: { index: number; createdBilling: any }) {
    const createdBillings = this.createdBillings;
    createdBillings.splice(data.index, 1, data.createdBilling);
    this.context.commit("mutateCreatedBillings", createdBillings);
  }

  @Action
  async createBillingByThgId(data: { thgId: string; billingType: BillingTypeEnum }): Promise<void> {
    const created = await billingService.createBillingForThg(data.thgId, false, true, true, data.billingType);
    this.context.commit("mutateCreatedBilling", created);
  }

  @Action
  async forceCreateBillingByThgId(data: { thgId: string; billingType: BillingTypeEnum }): Promise<void> {
    const created = await billingService.createBillingForThg(data.thgId, true, true, true, data.billingType);
    this.context.commit("mutateCreatedBilling", created);
  }

  @Action
  async getPdf(billingNumber: number): Promise<string> {
    const billingPdf = await billingService.getPdf(billingNumber);
    this.context.commit("mutateBillingPdf", billingPdf.url);
    return billingPdf.url;
  }

  @Action
  async updateAccountingItem(data: { billingNumber: number; accountingItem: ThgAccountingRecordItemViewmodelGen[] }) {
    const accounting = await billingService.updateAccountingItem(data.billingNumber, data.accountingItem);
    this.context.commit("mutateBillingAccounting", accounting);
  }

  @Action
  async regenerateAccountingItem(billingNumber: number) {
    const accounting = await billingService.generateAccountingItem(billingNumber);
    this.context.commit("mutateBillingAccounting", accounting);
  }

  @Action
  async regeneratePdf(billingNumber: number) {
    const updated = await billingService.generatePdf(billingNumber);
    this.context.commit("mutateBillingPdf", updated.url);
  }

  @Action
  async publish(data: { billingNumber: number; publish: boolean; notifyUsers: boolean }) {
    await billingService.publish(data.billingNumber, data.publish, data.notifyUsers);
  }

  @Action
  async publishBatch(data: ThgPublishBillingsDtoGen) {
    return billingService.publishBatch(data);
  }

  @Action
  async getBilling(billingNumber: number) {
    const billing = await billingService.getBilling(billingNumber);
    this.context.commit("mutateCreatedBilling", billing);

    return billing;
  }

  @Action
  async getAllBillings() {
    const billings = await billingService.getAllBillings();
    billings.sort((a, b) => (a.billingNumber > b.billingNumber ? -1 : 1));
    this.context.commit("mutateAllBillings", billings);
  }

  @Action
  async dispatchBillingCsv(billingNumbers: number[]) {
    await billingService.dispatchBillingCsv(billingNumbers);
  }

  @Action
  async dispatchCreditorCsv(accountNumbers: string[]) {
    await billingService.dispatchCreditorCsv(accountNumbers);
  }

  @Action
  async getCsvAccess(operationId: string): Promise<ThgUrlViewmodelGen> {
    return billingService.getCsvByOperationId(operationId);
  }

  @Action
  async uploadPdf(data: { billingNumber: number; file: File }) {
    await billingService.uploadPdf(data.billingNumber, data.file);
  }

  @Action
  async createBilling(dto: ThgCreateBillingThgDtoGen) {
    const createdBilling = await billingService.createBilling(dto);

    this.context.commit("mutateCreatedBilling", createdBilling);
  }

  @Action
  async createThgBillingCancellation(billingNumber: number): Promise<ThgBillingViewmodelGen> {
    const created = await billingService.createBillingForThg(
      billingNumber.toString(),
      true,
      true,
      true,
      BillingTypeEnum.CANCELLATION
    );

    this.context.commit("mutateCreatedBilling", created);

    return created;
  }

  @Action
  async check(): Promise<ThgOperationIdViewModelGen> {
    return billingService.check();
  }
}

export const BillingModule = getModule(BillingStore);
